const CHINESE_REGEX = /[\u3400-\u9FBF]/;

/**
 * Preferred BCP-47 tags used when constructing SpeechSynthesisUtterance.lang.
 * Voice matching uses a broader scorer (see findBestVoice).
 */
export const VOICE_LANGUAGES = {
    cantonese: {
        order: 0,
        key: 'cantonese',
        preferredLang: 'zh-HK',
        codes: ['zh-HK', 'zh-hk', 'yue-HK', 'yue_HK', 'yue_HK_#Hant'],
        label: '粵',
        name: 'Cantonese',
        missingVoiceMessage:
            'Cantonese voice not found on this device. On iPhone: Settings → Accessibility → Spoken Content → Voices → Chinese → download a Hong Kong voice.',
    },
    mandarin: {
        order: 1,
        key: 'mandarin',
        preferredLang: 'zh-CN',
        codes: ['zh-CN', 'zh-cn', 'zh-Hans', 'zh-Hans-CN', 'zh_CN_#Hans', 'cmn-CN'],
        label: '普',
        name: 'Mandarin',
        missingVoiceMessage:
            'Mandarin voice not found on this device. On iPhone: Settings → Accessibility → Spoken Content → Voices → Chinese → download a Mainland China (普通话) voice. Without it, iOS may speak Cantonese instead.',
    },
    english: {
        order: 2,
        key: 'english',
        preferredLang: 'en-GB',
        codes: ['en-GB', 'en_GB', 'en-US', 'en_US', 'en'],
        label: 'Eng',
        name: 'English',
        missingVoiceMessage:
            'English voice not found on this device. Check Spoken Content voices in Settings.',
    },
};

export const isChinese = (text) => {
    if (Boolean(text) && text.match(CHINESE_REGEX)) {
        return true;
    }
    return false;
};

export const getVoiceLanguageCodeByKey = (voiceKey) => {
    return VOICE_LANGUAGES[voiceKey]?.codes;
};

export const getVoiceLanguageKeyByText = (text) => {
    return Boolean(isChinese(text)) ? 'cantonese' : 'english';
};

/** @deprecated Prefer getVoiceLanguageKeyByText + findBestVoice */
export const getVoiceLanguageCodeByText = (text) => {
    const key = getVoiceLanguageKeyByText(text);
    return VOICE_LANGUAGES[key]?.codes || [];
};

export const getChineseExplanation = async (text) => {
    let firstCharacter = text.substring(0, 1);
    let response = await fetch(`/json/chinese-dictionary/${firstCharacter}.json`);
    let json = await response.json();
    return json[text];
};

function normalizeLangTag(lang) {
    return String(lang || '')
        .trim()
        .toLowerCase()
        .replace(/_/g, '-')
        .replace(/#/g, '-');
}

function voiceSearchText(voice) {
    return [
        voice?.lang,
        voice?.name,
        voice?.voiceURI,
    ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .replace(/_/g, '-')
        .replace(/\s+/g, ' ');
}

function isCantoneseVoice(lang, haystack) {
    return (
        lang.startsWith('zh-hk')
        || lang.startsWith('yue')
        || lang.includes('yue-hk')
        || /(^|[^a-z])(yue)([^a-z]|$)/.test(haystack)
        || /cantonese|廣東話|粤语|粵語|hong kong|hongkong|sinji|sin-ji/.test(haystack)
    );
}

function isMandarinVoice(lang, haystack) {
    // Explicit Mandarin / Putonghua / Mainland / Taiwan indicators (including common iOS voice names)
    if (
        lang.startsWith('zh-cn')
        || lang.includes('zh-hans')
        || lang.startsWith('cmn')
        || lang.startsWith('zh-tw')
        || lang.includes('zh-hant-tw')
        || /mandarin|putonghua|普通话|普通話|中国大陆|china mainland|taiwan|台灣|台湾/.test(haystack)
        || /tingting|ting-ting|ting ting|yushu|yu-shu|limu|li-mu|meijia|mei-jia|lilian|li-lian/.test(haystack)
    ) {
        return true;
    }
    return false;
}

/**
 * Score a browser voice for a logical language key.
 * Mandarin never accepts generic zh-* or HK/Cantonese voices — that was causing iPhone 普 → 粵.
 */
export function scoreVoiceForLanguage(voice, languageKey) {
    if (!voice) {
        return -Infinity;
    }

    const lang = normalizeLangTag(voice.lang);
    const haystack = voiceSearchText(voice);
    const localBonus = voice.localService ? 25 : 0;

    if (languageKey === 'cantonese') {
        if (isCantoneseVoice(lang, haystack)) {
            return 200 + localBonus;
        }
        if (isMandarinVoice(lang, haystack)) {
            return -100;
        }
        return -1;
    }

    if (languageKey === 'mandarin') {
        // Hard reject Cantonese / HK voices first
        if (isCantoneseVoice(lang, haystack)) {
            return -100;
        }
        if (isMandarinVoice(lang, haystack)) {
            // Prefer Mainland over Taiwan slightly
            if (lang.startsWith('zh-cn') || lang.includes('zh-hans') || /tingting|putonghua|普通话|普通話|china/.test(haystack)) {
                return 220 + localBonus;
            }
            if (lang.startsWith('zh-tw') || /taiwan|台灣|台湾|meijia/.test(haystack)) {
                return 150 + localBonus;
            }
            return 200 + localBonus;
        }
        // Do NOT fall back to generic "zh" — on HK iPhones that is often Cantonese.
        return -1;
    }

    if (languageKey === 'english') {
        if (lang === 'en-gb' || lang.startsWith('en-gb')) {
            return 200 + localBonus;
        }
        if (lang === 'en-us' || lang.startsWith('en-us')) {
            return 160 + localBonus;
        }
        if (lang === 'en-au' || lang.startsWith('en-au') || lang === 'en-ie' || lang.startsWith('en-ie')) {
            return 140 + localBonus;
        }
        if (lang === 'en' || lang.startsWith('en-')) {
            return 100 + localBonus;
        }
        return -1;
    }

    return -1;
}

/** Always prefer a live speechSynthesis list (iOS often hydrates late). */
export function getAvailableVoices(voicesFromHook = []) {
    let live = [];
    if (typeof window !== 'undefined' && window.speechSynthesis) {
        live = window.speechSynthesis.getVoices() || [];
    }
    if (live.length > 0) {
        return live;
    }
    if (Array.isArray(voicesFromHook) && voicesFromHook.length > 0) {
        return voicesFromHook;
    }
    return [];
}

export function findBestVoice(voices, languageKey) {
    const available = getAvailableVoices(voices);
    let best = null;
    let bestScore = 0;

    available.forEach((voice) => {
        const score = scoreVoiceForLanguage(voice, languageKey);
        if (score > bestScore) {
            bestScore = score;
            best = voice;
        }
    });

    return bestScore > 0 ? best : null;
}

export function getPreferredSpeechLang(languageKey) {
    return VOICE_LANGUAGES[languageKey]?.preferredLang || 'en-GB';
}

/**
 * Speak text only when a matching voice is found for Chinese languages.
 * Speaking Mandarin with lang=zh-CN but no voice makes iOS fall back to Cantonese on HK devices.
 *
 * @returns {{ ok: boolean, error?: string, voiceName?: string }}
 */
export function speakTextWithLanguage({
    text,
    languageKey,
    voices = [],
    rate = 1,
    pitch = 1,
    volume = 1,
    onEnd,
    onError,
}) {
    if (typeof window === 'undefined' || !window.speechSynthesis) {
        const error = 'Speech synthesis is not supported on this device.';
        if (onError) {
            onError(new Error(error));
        }
        return { ok: false, error };
    }

    // Force a fresh voice list — critical on iOS Safari
    const availableVoices = getAvailableVoices(voices);
    const preferredLang = getPreferredSpeechLang(languageKey);
    const voice = findBestVoice(availableVoices, languageKey);
    const requiresMatchedVoice = languageKey === 'cantonese' || languageKey === 'mandarin';

    if (requiresMatchedVoice && !voice) {
        const error = VOICE_LANGUAGES[languageKey]?.missingVoiceMessage
            || `No suitable ${languageKey} voice found on this device.`;
        if (onError) {
            onError(new Error(error));
        }
        if (onEnd) {
            onEnd();
        }
        return { ok: false, error };
    }

    window.speechSynthesis.cancel();

    const utterance = new window.SpeechSynthesisUtterance(String(text || ''));
    utterance.rate = rate;
    utterance.pitch = pitch;
    utterance.volume = volume;

    if (voice) {
        utterance.voice = voice;
        utterance.lang = voice.lang || preferredLang;
    } else {
        // English-only soft fallback
        utterance.lang = preferredLang;
    }

    utterance.onend = () => {
        if (onEnd) {
            onEnd();
        }
    };
    utterance.onerror = (event) => {
        if (onError) {
            onError(event?.error || event);
        }
        if (onEnd) {
            onEnd();
        }
    };

    // iOS sometimes needs a tiny delay after cancel() before speak()
    window.setTimeout(() => {
        window.speechSynthesis.speak(utterance);
    }, 40);

    return { ok: true, voiceName: voice?.name || null };
}
